import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { Badge } from "@/components/common/DataTable";
import { crmService } from "@/services";
import {
  PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis,
} from "recharts";
import { UserPlus, TrendingUp, DollarSign, Target, CheckCircle2, ListChecks, Loader2 } from "lucide-react";
import { formatCurrency } from "@/lib/currency";

const COLORS = ["#0d9488", "#06b6d4", "#f59e0b", "#8b5cf6", "#ec4899", "#64748b"];
const fmt = (v) => formatCurrency(v);

function Kpi({ label, value, icon: Icon, color }) {
  return (
    <div className="bg-card rounded-xl border border-border p-5 shadow-sm animate-rise" data-testid={`crm-kpi-${label}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
        <Icon className={`h-5 w-5 ${color}`} />
      </div>
      <p className="text-2xl font-bold text-foreground mt-3">{value ?? "—"}</p>
    </div>
  );
}

function Card({ title, children, testid }) {
  return (
    <div className="bg-card rounded-xl border border-border p-5 shadow-sm" data-testid={testid}>
      <h3 className="font-semibold text-foreground mb-4">{title}</h3>
      {children}
    </div>
  );
}

export default function CrmDashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { crmService.dashboard().then(setData).catch(() => {}).finally(() => setLoading(false)); }, []);

  if (loading) return <div className="flex justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const t = data?.totals || {};
  const bySource = (data?.leadsBySource || []).filter((s) => s.value > 0);
  const byStage = (data?.opportunitiesByStage || []).filter((s) => s.value > 0);
  const recentLeads = data?.recentLeads || [];
  const tasksDue = data?.tasksDue || [];

  return (
    <div data-testid="crm-dashboard-page">
      <PageHeader title="CRM Dashboard" subtitle="Sales intelligence — leads, pipeline and activities at a glance" />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-7">
        <Kpi label="Leads" value={t.leads} icon={UserPlus} color="text-primary" />
        <Kpi label="Qualified" value={t.qualifiedLeads} icon={CheckCircle2} color="text-emerald-500" />
        <Kpi label="Open Opps" value={t.openOpportunities} icon={TrendingUp} color="text-sky-500" />
        <Kpi label="Pipeline" value={fmt(t.pipelineValue)} icon={DollarSign} color="text-amber-500" />
        <Kpi label="Open Tasks" value={t.tasksOpen} icon={ListChecks} color="text-violet-500" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <Card title="Leads by Source" testid="crm-by-source">
          {bySource.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={bySource} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {bySource.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card title="Opportunities by Stage" testid="crm-by-stage">
          {byStage.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={byStage}>
                <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} angle={-15} textAnchor="end" height={50} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="value" radius={[6, 6, 0, 0]} fill="#0d9488" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Recent Leads" testid="crm-recent-leads">
          <div className="space-y-2.5 max-h-72 overflow-y-auto">
            {recentLeads.length === 0 && <Empty />}
            {recentLeads.map((l) => (
              <button key={l.id} onClick={() => navigate(`/admin/leads/${l.id}`)}
                className="w-full flex items-center justify-between text-sm border-b border-border pb-2 hover:text-primary transition-colors text-left"
                data-testid={`crm-recent-lead-${l.id}`}>
                <span className="font-medium text-foreground">{l.fullName}</span>
                <Badge color="blue">{l.status}</Badge>
              </button>
            ))}
          </div>
        </Card>
        <Card title="Tasks & Follow-ups Due" testid="crm-tasks-due">
          <div className="space-y-2.5 max-h-72 overflow-y-auto">
            {tasksDue.length === 0 && <Empty text="No open tasks" />}
            {tasksDue.map((task) => (
              <div key={task.id} className="flex items-center justify-between text-sm border-b border-border pb-2">
                <span className="font-medium text-foreground">{task.title}</span>
                <Badge color={task.priority === "High" ? "red" : task.priority === "Low" ? "slate" : "amber"}>{task.priority}</Badge>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function Empty({ text = "No data yet" }) {
  return <p className="text-sm text-muted-foreground py-10 text-center">{text}</p>;
}
